import { useMutation, useQuery } from "@apollo/client";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import ZoomIn from "@mui/icons-material/ZoomIn";
import ZoomOut from "@mui/icons-material/ZoomOut";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { GetContainerLabelQuery, LabelExportFormat, LabelFormat } from "generated-graphql/graphql";
import { ReactElement, useEffect, useRef, useState } from "react";
import { downloadFromUrl } from "util/download";

import {
  EXPORT_CONTAINER_LABELS_MUTATION,
  GET_CONTAINER_LABEL_QUERY,
  SET_CONTAINER_LABEL_NOTE_MUTATION,
} from "../api";
import "./containerLabelPrint.css";
import { FormatRadioGroup } from "./LabelFormatControls";
import { LabelSizeOption, PREVIEW_SIZE_BY_LABEL_SIZE } from "./labelFormState";
import { isDotProfileIncomplete } from "./labelPreviewContent";
import { LabelPreviewStage } from "./LabelPreviewStage";
import { Eyebrow } from "./LabelScreenTypography";

type ContainerLabelData = NonNullable<GetContainerLabelQuery["wasteContainer"]>;

/** Toggled on `document.body` only while this dialog's content is mounted.
 * `containerLabelPrint.css`'s app-wide selectors (the "hide everything but
 * the print root" rule, the label's `@page` size) are gated behind this
 * class so they only ever apply to a print started from this dialog —
 * without it, they'd apply to every print in the app, since this CSS module
 * is bundled globally (this app never lazy-loads route components, so every
 * route's side-effect CSS imports end up in the same global stylesheet). */
const PRINT_ACTIVE_BODY_CLASS = "container-label-printing";

/** A fixed stage so the dialog holds still: the label's own size changes with
 * the format (a QR-only label is a fraction of a full one) and with the
 * reader's zoom, and none of that should move the controls beside it. */
const PREVIEW_STAGE_HEIGHT = 420;

/** Zoom steps to two decimals, so repeated +/- can't drift onto values like
 * 1.7500000000000002 and render a nonsense percentage. */
function roundZoom(value: number): number {
  return Math.round(value * 100) / 100;
}

const ZOOM_STEP = 0.25;
const ZOOM_BOUNDS = { min: 0.5, max: 2 };

function PreviewCard({
  container,
  layout,
  branded,
  note,
  size,
}: {
  container: ContainerLabelData;
  layout: LabelFormat;
  branded: boolean;
  note: string;
  size: LabelSizeOption;
}): ReactElement {
  const [zoomFactor, setZoomFactor] = useState(1);
  const stepZoom = (delta: number) =>
    setZoomFactor((current) =>
      Math.min(ZOOM_BOUNDS.max, Math.max(ZOOM_BOUNDS.min, roundZoom(current + delta)))
    );
  const dotIncomplete = isDotProfileIncomplete(container.label);
  const printRootClassName = [
    "container-label-print-root",
    size === "letter" ? "label-size-letter" : undefined,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Box>
      {dotIncomplete && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          DOT profile incomplete — label will print without hazard marking
        </Alert>
      )}

      {/* The artifact is what the reader opened this to check, so it leads and
          gets the full width; the choices that change it sit underneath. */}
      <Stack alignItems="stretch">
        <Box sx={{ width: "100%" }}>
          <LabelPreviewStage
            printRootClassName={printRootClassName}
            // Tighter than the stage's roomy full-page default: in a dialog
            // that padding is space the controls need.
            stagePadding={2}
            content={container.label}
            layout={layout}
            branded={branded}
            noteOverride={note}
            size={PREVIEW_SIZE_BY_LABEL_SIZE[size]}
            containerType={container.containerType}
            capacity={container.capacity}
            capacityUnit={container.capacityUnit}
            zoomFactor={zoomFactor}
            // Fixed so the dialog doesn't resize under the reader when the
            // label's own dimensions change -- a QR-only label is a fraction
            // of a full one's height.
            stageHeight={PREVIEW_STAGE_HEIGHT}
          />
        </Box>
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          justifyContent="flex-end"
          sx={{ mt: 1 }}
        >
          <Tooltip title="Zoom out">
            <span>
              <IconButton
                size="small"
                aria-label="Zoom out"
                disabled={zoomFactor <= ZOOM_BOUNDS.min}
                onClick={() => stepZoom(-ZOOM_STEP)}
              >
                <ZoomOut fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ minWidth: 44, textAlign: "center" }}
          >
            {Math.round(zoomFactor * 100)}%
          </Typography>
          <Tooltip title="Zoom in">
            <span>
              <IconButton
                size="small"
                aria-label="Zoom in"
                disabled={zoomFactor >= ZOOM_BOUNDS.max}
                onClick={() => stepZoom(ZOOM_STEP)}
              >
                <ZoomIn fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );
}

/** The Print & format card's mutation plumbing (save note / export),
 * split out of `ContainerLabelBody` so that component stays a plain layout. */
function useLabelActions(
  container: ContainerLabelData,
  {
    note,
    layout,
    branded,
  }: {
    note: string;
    layout: LabelFormat;
    branded: boolean;
  }
) {
  const alerts = useAlerts();
  const [saveNote] = useMutation(SET_CONTAINER_LABEL_NOTE_MUTATION);
  const [exportLabels, { loading: exporting }] = useMutation(EXPORT_CONTAINER_LABELS_MUTATION);
  // Bridges the gap between clicking Export and `exportLabels` actually
  // firing below -- see `handleExport`. Without it, the Export button's
  // `loading` prop (driven by `exporting`, the export mutation's own
  // `loading`) would stay false while a dirty note save is in flight,
  // reading as if the click did nothing.
  const [savingNoteBeforeExport, setSavingNoteBeforeExport] = useState(false);

  // The no-op guard can't compare against `container.labelNote` -- that prop
  // stays stale until the save's response lands in the cache, so a
  // blur-refocus-blur before it resolves would see the same "unsaved" note
  // both times and fire an identical second mutation. Tracking the
  // last-submitted value in a ref (updated synchronously, before the
  // mutation call) closes that window; reverting it on error lets a retry
  // fire again.
  const lastSubmittedNoteRef = useRef(container.labelNote ?? "");
  // The save triggered by the note field's own blur -- when Export is
  // clicked with the note field focused, that blur fires (and this ref gets
  // set) synchronously before Export's own click handler runs, so
  // `commitNoteIfDirty` below sees the note as already "submitted" and
  // needs this to still await the save that's actually in flight.
  const pendingNoteSaveRef = useRef<Promise<unknown> | null>(null);

  /** Saves the note if it's changed since the last submit, returning a
   * promise that resolves once that save (new or already in flight) settles.
   * Resolves immediately when there's nothing to save. */
  const commitNoteIfDirty = (): Promise<unknown> => {
    if (note === lastSubmittedNoteRef.current) {
      return pendingNoteSaveRef.current ?? Promise.resolve();
    }
    const previouslySubmitted = lastSubmittedNoteRef.current;
    lastSubmittedNoteRef.current = note;
    const savePromise = saveNote({
      variables: {
        containerId: container.id,
        note: note.length > 0 ? note : null,
      },
      onError: (mutationError) => {
        lastSubmittedNoteRef.current = previouslySubmitted;
        alerts.error(mutationError.message || "Failed to save the label note.");
      },
    });
    pendingNoteSaveRef.current = savePromise;
    return savePromise;
  };

  const handleNoteBlur = () => {
    void commitNoteIfDirty();
  };

  const runExport = (fileType: LabelExportFormat) => {
    exportLabels({
      variables: {
        containerIds: [container.id],
        layout,
        fileType,
        branded,
      },
      onCompleted: (result) => {
        const extension = fileType === LabelExportFormat.Png ? "png" : "pdf";
        void downloadFromUrl(
          result.exportContainerLabels.url,
          `container-label-${container.containerLabel}.${extension}`
        );
      },
      onError: (mutationError) => {
        alerts.error(mutationError.message || "Failed to export the label.");
      },
    });
  };

  // Clicking Export blurs the note field first (moving focus to the
  // button), which independently fires a save -- racing it against the
  // export would let the exported PDF miss a note the preview already
  // shows. Awaiting the (possibly already in-flight) save first guarantees
  // the export always reflects the note as of the click, whether it failed
  // or not -- a failed save already surfaces its own alert above, and
  // blocking the export on top of that wouldn't help the user recover.
  const handleExport = (fileType: LabelExportFormat) => {
    setSavingNoteBeforeExport(true);
    void commitNoteIfDirty()
      .catch(() => undefined)
      .finally(() => {
        setSavingNoteBeforeExport(false);
        runExport(fileType);
      });
  };

  return {
    exporting: exporting || savingNoteBeforeExport,
    handleNoteBlur,
    handleExport,
  };
}

/**
 * One action with a choice of flavor, as a split button: the main half runs
 * what it says, the caret changes which. Two plain buttons per action read as
 * four unrelated things when they are really two, each with a format to pick.
 */
function SplitActionButton<T extends string>({
  options,
  value,
  onChange,
  onRun,
  variant,
  loading,
  chooseLabel,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (next: T) => void;
  onRun: (next: T) => void;
  variant: "outlined" | "contained";
  loading?: boolean;
  chooseLabel: string;
}): ReactElement {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const selected = options.find((option) => option.value === value) ?? options[0];

  return (
    <>
      <ButtonGroup variant={variant}>
        <Button onClick={() => onRun(selected.value)} loading={loading}>
          {selected.label}
        </Button>
        <Button
          size="small"
          aria-label={chooseLabel}
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          <ArrowDropDown fontSize="small" />
        </Button>
      </ButtonGroup>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        {options.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === value}
            onClick={() => {
              onChange(option.value);
              setAnchorEl(null);
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/** The dialog's two actions, built outside the component so its body stays a
 * layout rather than a layout plus a footer. */
function labelDialogActions({
  fileType,
  setFileType,
  handleExport,
  exporting,
  size,
  setSize,
  printAt,
}: {
  fileType: LabelExportFormat;
  setFileType: (next: LabelExportFormat) => void;
  handleExport: (next: LabelExportFormat) => void;
  exporting: boolean;
  size: LabelSizeOption;
  setSize: (next: LabelSizeOption) => void;
  printAt: (next: LabelSizeOption) => void;
}): ReactElement[] {
  // Two actions, each with a format to choose -- not four buttons. File type
  // only reaches the export mutation and print size only reaches the preview
  // and the print stylesheet's @page, so neither belongs in the content where
  // it read as governing both.
  return [
    <Tooltip key="download" title="Downloads always render the 4 × 6 label.">
      <span>
        <SplitActionButton
          variant="outlined"
          chooseLabel="Choose download format"
          value={fileType}
          onChange={setFileType}
          onRun={handleExport}
          loading={exporting}
          options={[
            { value: LabelExportFormat.Pdf, label: "Download PDF" },
            { value: LabelExportFormat.Png, label: "Download PNG" },
          ]}
        />
      </span>
    </Tooltip>,
    <SplitActionButton
      key="print"
      variant="contained"
      chooseLabel="Choose print size"
      options={[
        { value: "4x6" as LabelSizeOption, label: "Print 4 × 6" },
        { value: "letter" as LabelSizeOption, label: "Print letter" },
      ]}
      value={size}
      onChange={setSize}
      onRun={printAt}
    />,
  ];
}

/**
 * The loaded dialog: format controls beside the live preview, with the two
 * things a reader came to do — print, or save a file — in the action bar.
 *
 * Those two live in the bar rather than beside the selects they belong to
 * because the controls column runs longer than the dialog is tall; inline, the
 * Print button on a print dialog sat below the fold.
 */
function LoadedLabelDialog({
  container,
  open,
  onClose,
}: {
  container: ContainerLabelData;
  open: boolean;
  onClose: () => void;
}): ReactElement {
  // Only while this content is on screen should its print rules be able to
  // fire -- see the PRINT_ACTIVE_BODY_CLASS comment above.
  useEffect(() => {
    document.body.classList.add(PRINT_ACTIVE_BODY_CLASS);
    return () => {
      document.body.classList.remove(PRINT_ACTIVE_BODY_CLASS);
    };
  }, []);

  const [layout, setLayout] = useState<LabelFormat>(
    container.facility?.defaultLabelFormat ?? LabelFormat.Full
  );
  const [fileType, setFileType] = useState<LabelExportFormat>(LabelExportFormat.Pdf);
  const [size, setSize] = useState<LabelSizeOption>("4x6");
  // No UI sets this anymore -- see the "Not showing the Encamp-logo switch"
  // comment in PrintFormatCard.tsx -- but it stays real state (not a literal
  // `true`) so re-enabling the switch is just adding back a control wired to
  // it.
  const [branded] = useState(true);
  const [note, setNote] = useState(container.labelNote ?? "");

  // The QR-only layout prints just the branded code, so the note has nowhere
  // to go on it.
  const noteApplies = layout !== LabelFormat.QrOnly;

  const { exporting, handleNoteBlur, handleExport } = useLabelActions(container, {
    note,
    layout,
    branded,
  });

  // Each Print button names the stock it prints on, so it has to set that size
  // and let the preview (and the print stylesheet's @page, which keys off it)
  // re-render before the browser's print dialog opens.
  const [pendingPrintSize, setPendingPrintSize] = useState<LabelSizeOption | null>(null);
  useEffect(() => {
    if (pendingPrintSize === null || size !== pendingPrintSize) {
      return;
    }
    setPendingPrintSize(null);
    window.print();
  }, [pendingPrintSize, size]);

  const printAt = (nextSize: LabelSizeOption) => {
    setSize(nextSize);
    setPendingPrintSize(nextSize);
  };

  return (
    <LabelDialogShell
      open={open}
      onClose={onClose}
      actions={labelDialogActions({
        fileType,
        setFileType,
        handleExport,
        exporting,
        size,
        setSize,
        printAt,
      })}
    >
      <Grid container spacing={4} alignItems="flex-start" sx={{ pt: 1 }}>
        <Grid size={{ xs: 12, md: 5 }}>
          {/* "Label format", not "Print format": this is the one choice that
              governs both actions -- it is sent to the export mutation and it
              is what gets printed. */}
          <Eyebrow mt={0}>Label format</Eyebrow>
          <FormatRadioGroup layout={layout} onChange={setLayout} />
          {/* Hidden rather than disabled for QR-code-only, which prints no
              note at all. The value stays in state, so toggling back to Full
              label brings back whatever was typed. */}
          {noteApplies && (
            <Box sx={{ mt: 3 }}>
              <Eyebrow mt={0}>Label note</Eyebrow>
              <TextField
                multiline
                minRows={3}
                fullWidth
                // The visible label is the Eyebrow above, so name the control
                // itself here -- on TextField, `aria-label` would land on the
                // wrapper rather than the textarea.
                slotProps={{ htmlInput: { "aria-label": "Label note" } }}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                onBlur={handleNoteBlur}
                helperText="Prints on the label only"
                sx={{ mt: 1 }}
              />
            </Box>
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <PreviewCard
            container={container}
            layout={layout}
            branded={branded}
            note={note}
            size={size}
          />
        </Grid>
      </Grid>
    </LabelDialogShell>
  );
}

/**
 * The dialog chrome both states share, so the title, width and Done button
 * can't drift between the loaded dialog and the one still fetching.
 */
function LabelDialogShell({
  open,
  onClose,
  actions = [],
  children,
}: {
  open: boolean;
  onClose: () => void;
  actions?: ReactElement[];
  children: ReactElement;
}): ReactElement {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      // Without this the dialog sizes to its content, so the skeleton opens
      // narrow and snaps to full width the moment the container lands.
      fullWidth
      title="Print or download label"
      // Nothing here is staged: Print and Download act immediately and the
      // note saves on blur, so Save/Cancel would imply pending changes that
      // don't exist -- and with the two real actions in the bar, a Done button
      // next to them just competes. The corner X dismisses.
      hideCancelButton
      noPrimaryAction
      additionalPrimaryActionElements={actions}
    >
      {children}
    </Dialog>
  );
}

/** The dialog's shape before its data arrives -- the same two-pane split at
 * roughly the same heights the loaded body settles at, so opening it doesn't
 * jump. The identity line is a skeleton too: the real one is a subheader, and
 * without a stand-in everything below it shifts up a row. */
function ContainerLabelDialogSkeleton(): ReactElement {
  return (
    <Grid container spacing={4} alignItems="flex-start" sx={{ pt: 1 }}>
      <Grid size={{ xs: 12, md: 5 }}>
        {/* Label format: an eyebrow over the two option cards. */}
        <Skeleton variant="text" width={96} height={20} />
        <Skeleton variant="rectangular" height={86} sx={{ mt: 1, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={86} sx={{ mt: 1, borderRadius: 1 }} />
        {/* Label note. */}
        <Skeleton variant="text" width={80} height={20} sx={{ mt: 3 }} />
        <Skeleton variant="rectangular" height={100} sx={{ mt: 1, borderRadius: 1 }} />
      </Grid>
      <Grid size={{ xs: 12, md: 7 }}>
        {/* The stage is a fixed height whether or not it has a label in it, so
            matching it here is what keeps the dialog from resizing when the
            real artifact arrives. */}
        <Skeleton variant="rectangular" height={PREVIEW_STAGE_HEIGHT} sx={{ borderRadius: 2 }} />
        <Skeleton variant="text" width={110} height={32} sx={{ mt: 1, ml: "auto" }} />
      </Grid>
    </Grid>
  );
}

export type ContainerLabelDialogProps = {
  containerId: string | undefined;
  open: boolean;
  onClose: () => void;
};

/**
 * Everything you can do with a container's label: pick the format, check the
 * preview, add a note, then print or download it.
 *
 * A dialog rather than the page this used to be, because every one of those
 * choices belongs to the container the reader already has open -- sending them
 * to a separate route to make them meant losing the container behind it. It
 * still has a URL, so it can be linked to and survives a refresh.
 */
export function ContainerLabelDialog({
  containerId,
  open,
  onClose,
}: ContainerLabelDialogProps): ReactElement {
  const { data, loading } = useQuery(GET_CONTAINER_LABEL_QUERY, {
    variables: { id: containerId ?? "" },
    skip: !containerId || !open,
  });
  const container = data?.wasteContainer;

  // A loaded container gets its own dialog so the format state and the
  // Print/Download actions live together; the other two states share the same
  // shell with nothing to act on.
  if (container) {
    return <LoadedLabelDialog container={container} open={open} onClose={onClose} />;
  }

  return (
    <LabelDialogShell
      open={open}
      onClose={onClose}
      // Placeholders, not an empty bar: with no actions the shell drops the
      // whole footer, and the dialog grew by its height the moment the
      // container landed.
      actions={
        loading
          ? [
              <Button key="download" variant="outlined" disabled>
                Download PDF
              </Button>,
              <Button key="print" variant="contained" disabled>
                Print 4 × 6
              </Button>,
            ]
          : []
      }
    >
      {loading ? (
        <ContainerLabelDialogSkeleton />
      ) : (
        <Stack spacing={1} sx={{ py: 4 }} alignItems="center">
          <Typography variant="h6">Container not found</Typography>
          <Typography variant="body2" color="text.secondary">
            The container may have been archived, or the link may be incorrect.
          </Typography>
        </Stack>
      )}
    </LabelDialogShell>
  );
}
